/** External **/
import React from "react";
import _ from "lodash";

/** Design System **/
import { ToggleSwitch, Tooltip } from "@toddle-edu/ds-web";

/** Internal **/
import ACLStore from "lib/aclStore";
import Accordian from "UIComponents/Accordian";
import { useI18n } from "Hooks";
import { getSubTextFromCurriculumProgramType } from "InternalTools/modules/InternalToolUtils";
import { TODDLE_ANALYTICS_MODULES } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnContants";
import {
  getTooltipText,
  getLastSyncTimeProps,
  getConfigurationQueryFilters,
} from "AddOnManager/routes/OrgAddOn/components/ToddleAnalytics/modules/Utils";
import { readAddOnStatusForConfigValuesFromCache } from "AddOnManager/routes/OrgAddOn/modules/OrgAddOnHelpers";

/** Relative **/
import classes from "./CurriculumProgramAccordion.scss";

const styles = {
  headerStyle: {
    padding: "0px",
  },
  titleContainer: { fontSize: 16, fontWeight: "600" },
  mainContentContainerStyle: { marginBottom: "0px" },
};

const CurriculumProgramAccordion = props => {
  const {
    curriculumProgram = {},
    moduleStatusOriginalMapping = {},
    statusMapping = {},
    handleToggleSwitch,
    orgId,
  } = props;

  const { t } = useI18n();
  const { id, acronym, type, variant = "" } = curriculumProgram;

  const getModuleProps = ({ module }) => {
    const disabledCurriculums = _.get(module, "disabledCurriculums", []);
    const disabledVariants = _.get(module, "disabledVariants", []);
    const moduleName = _.get(module, "id");
    const mappingId = `${id}-${moduleName}`;

    const perm = _.get(module, "perm");
    let showModule = perm ? ACLStore.can(perm) : true;
    const modulePermConfigId = _.get(module, "modulePermConfigId");
    if (modulePermConfigId && showModule) {
      const configQueryFilters = getConfigurationQueryFilters({ orgId });
      const queryData = readAddOnStatusForConfigValuesFromCache({
        filters: configQueryFilters,
      });
      const data = _.get(
        queryData,
        "platform.getConfigurationValues.0.values",
        []
      );
      showModule = _.get(_.head(data), "value", false);
    }

    const isModuleDisabledForCurriculum =
      _.includes(disabledCurriculums, type) ||
      _.includes(disabledVariants, variant);

    const isChecked = statusMapping[mappingId];
    const disableToggle = _.get(module, "disableToggle", false);

    const isTooltipVisible = disableToggle || isModuleDisabledForCurriculum;

    const tooltip = getTooltipText({
      t,
      moduleName,
      acronym,
      curriculumProgramType: type,
      curriculumProgramVariant: variant,
    });

    const lastSyncTimeProps = getLastSyncTimeProps({
      t,
      orgId,
      moduleName,
      curriculumProgramId: id,
    });

    return {
      moduleName,
      showModule,
      isChecked,
      isTooltipVisible,
      tooltip,
      isModuleDisabledForCurriculum,
      disableToggle,
      lastSyncTimeProps,
      showLastSyncStatus: moduleStatusOriginalMapping[mappingId],
    };
  };

  return (
    <Accordian
      key={acronym}
      title={getSubTextFromCurriculumProgramType(acronym)}
      headerStyle={styles.headerStyle}
      customTitleStyle={styles.titleContainer}
      mainContentContainerStyle={styles.mainContentContainerStyle}
      mainContainerClass={classes.mainContainer}
      hideContainerBorder
      showLabelBorder={false}
      expandedContent={_.map(TODDLE_ANALYTICS_MODULES, module => {
        const {
          moduleName,
          showModule,
          isChecked,
          isTooltipVisible,
          tooltip,
          isModuleDisabledForCurriculum,
          disableToggle,
          lastSyncTimeProps,
          showLastSyncStatus,
        } = getModuleProps({ module });

        return showModule ? (
          <div className={classes.row}>
            <div className={classes.curriculumBox}>
              <div className="text-label">{t(module.label)}</div>
              {showLastSyncStatus ? (
                <div className={classes.lastSyncBox}>
                  {_.get(lastSyncTimeProps, "icon")}
                  <div
                    style={{ color: _.get(lastSyncTimeProps, "textColor") }}
                    className="text-body-s"
                  >
                    {_.get(lastSyncTimeProps, "text")}
                  </div>
                </div>
              ) : null}
            </div>
            <Tooltip
              tooltip={tooltip}
              visible={isTooltipVisible}
              placement={"left"}
            >
              <ToggleSwitch
                disabled={isModuleDisabledForCurriculum || disableToggle}
                checked={isChecked}
                onChange={() => handleToggleSwitch({ moduleName })}
              />
            </Tooltip>
          </div>
        ) : null;
      })}
    />
  );
};

export default CurriculumProgramAccordion;
